import React, { useState, useRef, useEffect } from "react";
import { useAuth } from "../contexts/AuthContext";
import "../styles/CodeInput.css";

interface CodeInputProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

export function CodeInput({ onSuccess, onError }: CodeInputProps) {
  const { verifyCode } = useAuth();
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCode(value);
    setError("");

    if (value.length === 6) {
      await handleVerifyCode(value);
    }
  };

  const handleVerifyCode = async (codeToVerify: string) => {
    setLoading(true);
    setError("");

    try {
      await verifyCode(codeToVerify);
      onSuccess?.();
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || err.message || "Invalid code";
      setError(errorMessage);
      onError?.(errorMessage);
      setCode("");
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && code.length === 0) {
      e.preventDefault();
    }
  };

  return (
    <div className="code-input-container">
      <label htmlFor="magic-code" className="code-input-label">
        Or enter your 6-digit code:
      </label>
      <input
        ref={inputRef}
        id="magic-code"
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        className={`code-input ${error ? "code-input-error" : ""} ${loading ? "code-input-loading" : ""}`}
        placeholder="000000"
        value={code}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={loading}
        maxLength={6}
        autoComplete="off"
      />
      {error && <div className="code-input-error-message">{error}</div>}
      {loading && <div className="code-input-hint">Verifying...</div>}
      {!loading && !error && code.length > 0 && code.length < 6 && (
        <div className="code-input-hint">{6 - code.length} more digits</div>
      )}
    </div>
  );
}
